import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { useCallback } from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { signInWithCustomToken } from "firebase/auth";

import { AuthStore } from "@/store/useAuthStore";
import { apiClient } from "@/api/api";
import { adminKeys } from "@/config/Query.Keys";
import { auth } from "@/config/firebase";
import {
  fetchAllAnimals,
  fetchAllFarmers,
  fetchAllFarms,
  fetchAllPhenotype,
  fetchAllSupervisors,
} from "@/features/Admin/Admin.api";

type Farm = {
  approvedAnimalRegistrations: string[];
  assignedSupervisor: string;
  city: string;
  country: string;
  createdAt: string;
  email: string;
  farmName: string;
  farmOwnerId: string;
  farmType: string;
  lastLogin: string;
  locations: object;
  monitoredAnimals: string[];
  ownerName: string;
  phoneNumber: string;
  registeredAnimals: string[];
  rejectedAnimalRegistrations: string[];
  state: string;
  status: string;
  updatedAt: string;
  zipCode: string;
  _id: string;
};

interface IAuthState extends JwtPayload {
  UserInfo: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
    role: string;
    status: string;
    ownFarm: boolean;
    farm: Farm;
    farmName: string;
  };
}

export const useRegister = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["authRegistered"],
    mutationFn: async (data: {
      firstname: string;
      lastname: string;
      email: string;
      role: string;
      password: string;
    }) => {
      const response = await apiClient.post("/registrationFarmer", data);

      return response.data;
    },
    retry: true,
    onSettled: () => {
      navigate("/login");
    },
    onSuccess: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["farmers"] });
      navigate("/login");
    }, [navigate, queryClient]),
    onError: useCallback((error: unknown) => {
      if (error instanceof AxiosError) {
        // Provide a default message if the specific one
        // isn't found
        const errorMessage =
          error.response?.data?.message ||
          "An unknown registration error occurred.";
        toast.error(errorMessage);
      } else {
        // Handle non-Axios errors as well
        toast.error("An unexpected error occurred.");
      }
    }, []),
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCredentials, setFarmInfo } = AuthStore();

  return useMutation({
    mutationKey: ["credentials"],
    mutationFn: async (data: any) => {
      // @ts-ignore
      const response = await apiClient.post("/login", data, { skipAuthRefresh: true });
      return response.data;
    },
    onSuccess: async (data) => {
      try {
        if (!data?.accessToken) throw new Error("Login response is missing an access token");
        const decoded = jwtDecode<IAuthState>(data.accessToken);
        console.log("Login successful");
        if (data) {
          console.log("Authenticating with the Blaze storage project...");
          try {
            await signInWithCustomToken(auth, data.firebaseToken);
            console.log("Firebase storage sign-in successful!");
          } catch (firebaseError) {
            console.error("Firebase Auth Error:", firebaseError);
            toast.error("Failed to authenticate with Firebase storage service");
          }

          setCredentials({
            id: decoded.UserInfo.id,
            token: data.accessToken,
            firstname: decoded.UserInfo.firstname,
            lastname: decoded.UserInfo.lastname,
            role: decoded.UserInfo.role,
            status: decoded.UserInfo.status,
            email: decoded.UserInfo.email,
            ownFarm: decoded.UserInfo.ownFarm,
            farmName: decoded.UserInfo.farmName,
          });

          if (decoded.UserInfo.farm) {
            const farm = decoded.UserInfo.farm;

            setFarmInfo({
              id: farm._id,
              approvedAnimalRegistrations: farm.approvedAnimalRegistrations,
              assignedSupervisor: farm.assignedSupervisor,
              city: farm.city,
              country: farm.country,
              createdAt: farm.createdAt,
              email: farm.email,
              farmName: farm.farmName,
              farmOwnerId: farm.farmOwnerId,
              farmType: farm.farmType,
              lastLogin: farm.lastLogin,
              locations: farm.locations,
              monitoredAnimals: farm.monitoredAnimals,
              ownerName: farm.ownerName,
              phoneNumber: farm.phoneNumber,
              registeredAnimals: farm.registeredAnimals,
              rejectedAnimalRegistrations: farm.rejectedAnimalRegistrations,
              state: farm.state,
              status: farm.status,
              updatedAt: farm.updatedAt,
              address: farm.zipCode,
            });
          }
        }

        apiClient.defaults.headers.common.authorization = `Bearer ${data.accessToken}`;

        // Route based on the freshly decoded JWT role — never read the
        // stale localStorage value which may still belong to the previous
        // user if the async logout clear hasn't finished yet.
        const role = decoded.UserInfo.role?.toLowerCase();

        if (role === "farmer") {
          navigate("/overview");
        } else if (role === "supervisor") {
          navigate("/supervisor/dashboard?type=Animals");
        } else if (role === "admin") {
          await Promise.all([
            queryClient.prefetchQuery({
              queryKey: adminKeys.allAnimals(),
              queryFn: fetchAllAnimals,
            }),
            queryClient.prefetchQuery({
              queryKey: adminKeys.allFarmers(),
              queryFn: fetchAllFarmers,
            }),
            queryClient.prefetchQuery({
              queryKey: adminKeys.allFarms(),
              queryFn: fetchAllFarms,
            }),
            queryClient.prefetchQuery({
              queryKey: adminKeys.allSupervisors(),
              queryFn: fetchAllSupervisors,
            }),
            queryClient.prefetchQuery({
              queryKey: adminKeys.phenotype(),
              queryFn: fetchAllPhenotype,
            }),
          ]);
          navigate("/admin");
        } else if (role === "guest") {
          await queryClient.prefetchQuery({
            queryKey: adminKeys.allAnimals(),
            queryFn: fetchAllAnimals,
          });
          navigate("/guest/dashboard");
        }
      } catch (error) {
        // throw Error()
        console.error("Login failed:", error);
      }
      // navigate(location.state?.from?.pathname || '/home',
      // {replace: true});
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(`${error?.response?.data?.error}`, {
          duration: 1000,
        });
      }
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logOut } = AuthStore();

  return useMutation({
    mutationKey: ["credentials"],
    mutationFn: async () => {
      try {
        await apiClient.post("/logout", {
          withCredentials: false,
        });
        localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
      } catch (error) {
        toast.error("Error logging out");
        throw error;
      }
    },
    onSuccess: () => {
      // Wipe the in-memory React Query store FIRST so the next user that
      // logs in never receives this session's cached data — regardless of
      // staleTime / gcTime settings or whether storage was already cleared.
      queryClient.clear();
      // Drop the stale Bearer token from apiClient so it cannot be sent
      // by any request that fires before the next login sets a new one.
      delete apiClient.defaults.headers.common.authorization;
      delete apiClient.defaults.headers.common.Authorization;
      logOut();
      localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
      caches
        .delete("https://silagreenagro-56f6b.web.app")
        .then(() => caches.delete("api-cache"))
        .then(() => caches.delete("dynamic-cache"))
        .then(() => caches.delete("static-cache"))
        .then(() => indexedDB.deleteDatabase("posts-store"))
        .then(() => localStorage.clear())
        .catch((error) => console.error("Error deleting cache:", error));
      indexedDB.deleteDatabase("posts-store");
      navigate("/login");
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(`${error.response?.data.message}`, {
          duration: 1000,
        });
      }
    },
  });
};

export const useRefresh = () => {
  const { setCredentials } = AuthStore();

  return useQuery({
    queryKey: ["refresh"],
    queryFn: async () => {
      try {
        console.log("useRefresh calling /refresh endpoint...");
        const response = await apiClient.get("/refresh");
        console.log("Refresh response status:", response.status);

        if (response.status === 200) {
          console.log("Refreshing Blaze storage authentication...");
          try {
            await signInWithCustomToken(auth, response.data.firebaseToken);
            console.log("Firebase storage refresh sign-in successful!");
          } catch (firebaseError) {
            console.error("Firebase Auth Error (Refresh):", firebaseError);
          }

          const decoded = jwtDecode<IAuthState>(response.data.accessToken);

          setCredentials({
            id: decoded.UserInfo.id,
            token: response.data.accessToken,
            firstname: decoded.UserInfo.firstname,
            lastname: decoded.UserInfo.lastname,
            role: decoded.UserInfo.role,
            status: decoded.UserInfo.status,
            email: decoded.UserInfo.email,
            ownFarm: decoded.UserInfo.ownFarm,
            farmName: decoded.UserInfo.farmName,
          });

          return response.data;
        }
      } catch (error) {
        throw error;
      }
    },
  });
};

export const useUpdateCredentials = () => {
  const queryClient = useQueryClient();
  const { setCredentials, userInfo } = AuthStore();

  return useMutation({
    mutationKey: ["updateCredentials"],
    mutationFn: async ({
      firstName,
      lastName,
      email,
      oldPassword,
      newPassword,
    }: {
      firstName: string;
      lastName: string;
      email: string;
      oldPassword?: string;
      newPassword?: string;
    }) => {
      try {
        const response = await apiClient.patch("/updateCredentials", {
          firstName,
          lastName,
          email,
          oldPassword,
          newPassword,
        });

        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: async (data) => {
      if (data?.user) {
        setCredentials({
          ...userInfo,
          id: data.user._id,
          firstname: data.user.firstName,
          lastname: data.user.lastName,
          email: data.user.email,
          role: data.user.role,
        });
        queryClient.setQueryData(["credentials"], data.user);
      }
      toast.success(data?.message || "Credentials updated successfully.");
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data.message || "Failed to update credentials.",
        );
      } else {
        toast.error("Failed to update credentials.");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
    },
  });
};

export function useUpdateCredentialsMutation() {
  const queryClient = useQueryClient();
  const { setCredentials } = AuthStore();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.put("/auth/update-credentials", data, {
        withCredentials: true, // if using cookies/JWT
      });
      return response.data.user;
    },
    onSuccess: (user) => {
      setCredentials(user);
      localStorage.setItem("credentials", JSON.stringify(user));
      queryClient.invalidateQueries({ queryKey: ["credentials"] }); // optional: refetch user queries
    },
  });
}
